import React from 'react';
import * as Sentry from "@sentry/react";
import { isNoiseException } from "./errorNoise";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const MODE = import.meta.env.MODE;
const VITE_SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

/**
 * Initialize Sentry for error tracking and performance monitoring
 * Only enabled in production environment
 */
/**
 * Which events are noise and must never reach Sentry.
 *
 * Exported, and therefore testable, because the previous version silently did
 * nothing: it matched its ignore list against `event.message`, which Sentry only
 * populates for captureMessage. Every entry on that list was written for a
 * captured EXCEPTION, where the text lives in `event.exception.values[].value` —
 * so the filter read an undefined field and passed everything through.
 * LIGHTMASTER-WEB-16 is the second entry on the list verbatim, and was reported
 * anyway for eleven days.
 */
export function shouldDropEvent(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint,
): Sentry.ErrorEvent | null {

    // Skip local development errors
    if (event.request?.url?.includes('localhost') ||
        event.request?.url?.includes('127.0.0.1')) {
      return null;
    }

    // The thrown value, stringified once and defensively. Calling .toString()
    // straight on it was the previous version's other latent bug: this runs on
    // EVERY event, and a thrown object with a throwing toString would take the
    // whole filter down with it — turning one bad error into no error reporting.
    let thrown = '';
    try {
      thrown = String(hint.originalException ?? '');
    } catch {
      thrown = '';
    }

    // Skip extension errors (common noise)
    if (thrown.includes('Extension')) {
      return null;
    }

    // Skip non-actionable errors: one shared classifier (src/lib/errorNoise.ts)
    // so Sentry and PostHog's $exception capture agree on what is noise.
    //
    // event.message is only set for captureMessage. For a captured EXCEPTION
    // the text lives in event.exception.values[].value — an ignore list that
    // only read event.message once matched nothing (LIGHTMASTER-WEB-16). Check
    // every exception with its stack frames, the message, and the raw thrown value.
    const exceptions = event.exception?.values ?? [];
    if (exceptions.some((v) => isNoiseException({
      type: v.type,
      value: v.value,
      filenames: (v.stacktrace?.frames ?? []).flatMap((f) => [f.filename, f.abs_path]),
      unhandledRejection: v.mechanism?.handled === false && /unhandledrejection/i.test(v.mechanism?.type ?? ''),
    }))) {
      return null;
    }
    if (event.message && isNoiseException({ value: event.message })) return null;
    if (thrown && isNoiseException({ value: thrown })) return null;

    return event;

}

export function initSentry() {
  // Only initialize Sentry in production or staging
  const shouldInitialize = SENTRY_DSN && MODE === 'production';

  if (!shouldInitialize) {
    console.log('Sentry initialization skipped (development mode)');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: MODE,

    // Performance monitoring - lower sample rate for cost control
    tracesSampleRate: 0.1,

    // Capture all errors for debugging
    sampleRate: 1.0,

    // Enhanced error reporting
    sendDefaultPii: false, // Don't send PII for privacy
    maxBreadcrumbs: 50,

    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || 'unknown',

    // Injected bridge scripts from in-app webviews (Instagram/Facebook — i.e.
    // Meta ad traffic) crash on their own and report through our onerror hook.
    ignoreErrors: [
      /webkit\.messageHandlers/,
      // The Android half of the same problem: a webview tears its JS bridge down
      // while the page is still running, and the injected script's own failure
      // reports through our onerror hook. LIGHTMASTER-WEB-15, 2 users, all
      // in-app browsers.
      /Java object is gone/,
      /Error invoking postMessage/,
    ],

    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),

      // Enhanced console logging
      Sentry.consoleLoggingIntegration({
        levels: ["warn", "error"]
      }),

      // Context integration for React components
      Sentry.contextLinesIntegration(),
    ],

    // Filter out development/local URLs and common noise
    beforeSend: shouldDropEvent,

    // Add additional context for debugging
    beforeBreadcrumb(breadcrumb) {
      // Filter out noisy breadcrumbs
      if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
        return null;
      }

      return breadcrumb;
    },

    // Set initial context
    initialScope: {
      tags: {
        component: "lightmaster-web",
        // Add Supabase project context
        supabase_env: VITE_SUPABASE_URL?.includes('localhost') ? 'local' : 'cloud'
      }
    }
  });

  console.log('Sentry initialized successfully');
}

/**
 * Set user context for error tracking
 * Call this after user authentication
 */
export function setSentryUser(user: {
  id: string;
  email?: string;
  role?: string;
  organizationId?: string;
}) {
  Sentry.setUser({
    id: user.id,
    // Don't include email for privacy
    username: user.email ? user.email.split('@')[0] : undefined,
    role: user.role,
    organizationId: user.organizationId,
  });
}

/**
 * Clear user context (on logout)
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Set organization context
 */
export function setSentryOrganization(organizationId: string, organizationName?: string) {
  Sentry.setTag('organization.id', organizationId);
  if (organizationName) {
    Sentry.setTag('organization.name', organizationName);
  }
}

/**
 * Add breadcrumb for user actions
 */
export function addSentryBreadcrumb(message: string, category?: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    message,
    category: category || 'user-action',
    level: 'info',
    data,
    timestamp: Date.now() / 1000,
  });
}

/**
 * Manually capture an exception with context
 */
export function captureException(error: Error, context?: Record<string, any>) {
  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        // setContext silently drops non-object values — scalars must go
        // through setExtra or they never reach the event.
        if (value && typeof value === 'object') {
          scope.setContext(key, value as Record<string, unknown>);
        } else {
          scope.setExtra(key, value);
        }
      });
      // Searchable in Sentry as error_code:"..." (tags are capped at 200 chars).
      if (typeof context.errorCode === 'string' && context.errorCode) {
        scope.setTag('error_code', context.errorCode.slice(0, 200));
      }
    }
    Sentry.captureException(error);
  });
}

/**
 * Start a performance span for monitoring (replacing deprecated startTransaction)
 */
export function startPerformanceSpan(name: string, op: string) {
  return Sentry.startSpan({ name, op }, () => {});
}

/**
 * Wrap async operations with performance monitoring
 */
export async function withPerformanceMonitoring<T>(
  name: string,
  op: string,
  operation: () => Promise<T>
): Promise<T> {
  return Sentry.startSpan(
    { name, op },
    async () => {
      try {
        return await operation();
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    }
  );
}

// Export Sentry instance for advanced usage
export { Sentry };